import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  getAllApplications,
  updateApplicationStatus,
  deleteApplication,
  openApplicationDocument,
} from '../../../services/jobApplicationService';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrapper = styled.div``;

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.65rem 1rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.93rem;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterSelect = styled.select`
  padding: 0.65rem 1rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.93rem;
  font-family: inherit;
  background: white;
  cursor: pointer;

  &:focus { outline: none; }
`;

const RefreshButton = styled.button`
  padding: 0.65rem 1.25rem;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  background: white;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const StatChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  background: ${props => {
    if (props.status === 'pending')     return '#fff3cd';
    if (props.status === 'reviewing')  return '#d1ecf1';
    if (props.status === 'shortlisted') return '#d4edda';
    if (props.status === 'rejected')   return '#f8d7da';
    if (props.status === 'offered')    return '#e2d9f3';
    return '#e9ecef';
  }};
  color: ${props => {
    if (props.status === 'pending')     return '#856404';
    if (props.status === 'reviewing')  return '#0c5460';
    if (props.status === 'shortlisted') return '#155724';
    if (props.status === 'rejected')   return '#721c24';
    if (props.status === 'offered')    return '#432874';
    return '#495057';
  }};
`;

const ApplicationCard = styled.div`
  background: white;
  border: 2px solid ${props => {
    if (props.status === 'shortlisted') return '#28a745';
    if (props.status === 'offered')     return '#6f42c1';
    if (props.status === 'rejected')    return '#dc3545';
    if (props.status === 'reviewing')   return '#17a2b8';
    return 'rgba(16,29,64,0.1)';
  }};
  border-radius: 14px;
  margin-bottom: 1.25rem;
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  gap: 1rem;
  flex-wrap: wrap;
  cursor: pointer;
  user-select: none;
`;

const CardLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const ApplicantName = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
`;

const ApplicantMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  font-size: 0.87rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
`;

const CardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => {
    if (props.status === 'pending')     return '#fff3cd';
    if (props.status === 'reviewing')  return '#d1ecf1';
    if (props.status === 'shortlisted') return '#d4edda';
    if (props.status === 'rejected')   return '#f8d7da';
    if (props.status === 'offered')    return '#e2d9f3';
    return '#e9ecef';
  }};
  color: ${props => {
    if (props.status === 'pending')     return '#856404';
    if (props.status === 'reviewing')  return '#0c5460';
    if (props.status === 'shortlisted') return '#155724';
    if (props.status === 'rejected')   return '#721c24';
    if (props.status === 'offered')    return '#432874';
    return '#495057';
  }};
`;

const ExpandToggle = styled.span`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'none'};
  display: inline-block;
  transition: transform 0.2s ease;
`;

const CardBody = styled.div`
  display: ${props => props.expanded ? 'block' : 'none'};
  padding: 0 1.5rem 1.5rem;
  border-top: 1px solid rgba(16,29,64,0.08);
`;

const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBlock = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem;
`;

const InfoLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
  margin-bottom: 0.4rem;
`;

const InfoValue = styled.div`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  word-break: break-word;
  line-height: 1.6;
`;

const CoverNoteBlock = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const CoverNoteText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  white-space: pre-wrap;
`;

const DocumentRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const DocButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.theme.colors.primary};
  background: white;
  color: ${props => props.theme.colors.primary};
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-1px);
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 0.9rem;
  border-radius: 8px;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid;
  transition: all 0.2s ease;

  ${props => {
    if (props.variant === 'shortlist') return `
      border-color: #28a745; background: white; color: #28a745;
      &:hover { background: #28a745; color: white; }
    `;
    if (props.variant === 'offer') return `
      border-color: #6f42c1; background: white; color: #6f42c1;
      &:hover { background: #6f42c1; color: white; }
    `;
    if (props.variant === 'reject') return `
      border-color: #dc3545; background: white; color: #dc3545;
      &:hover { background: #dc3545; color: white; }
    `;
    if (props.variant === 'review') return `
      border-color: #17a2b8; background: white; color: #17a2b8;
      &:hover { background: #17a2b8; color: white; }
    `;
    if (props.variant === 'pending') return `
      border-color: #ffc107; background: white; color: #856404;
      &:hover { background: #ffc107; color: #856404; }
    `;
    if (props.variant === 'delete') return `
      border-color: #6c757d; background: white; color: #6c757d;
      &:hover { background: #6c757d; color: white; }
    `;
    return '';
  }}

  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const NotesSection = styled.div`
  margin-top: 1rem;
`;

const NotesLabel = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.4rem;
`;

const NotesInput = styled.textarea`
  width: 100%;
  padding: 0.65rem 0.85rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  min-height: 70px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SaveNotesButton = styled.button`
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.primary};
  color: white;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Alert = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  font-size: 1rem;
`;

// ---------------------------------------------------------------------------
// Status order for display
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: 'pending',     label: '⏳ Pending' },
  { value: 'reviewing',  label: '🔍 Reviewing' },
  { value: 'shortlisted', label: '✅ Shortlisted' },
  { value: 'offered',    label: '🎉 Offered' },
  { value: 'rejected',   label: '❌ Rejected' },
];

// ---------------------------------------------------------------------------
// Sub-component: individual application card
// ---------------------------------------------------------------------------

const AppCard = ({ app, onStatusChange, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(app.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(null);
  const [localAlert, setLocalAlert] = useState(null);

  const showAlert = (type, message) => {
    setLocalAlert({ type, message });
    setTimeout(() => setLocalAlert(null), 4000);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await onStatusChange(app.id, newStatus, notes);
    } catch (err) {
      showAlert('error', err.message || 'Failed to update status.');
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await onStatusChange(app.id, app.status, notes);
      showAlert('success', 'Notes saved.');
    } catch (err) {
      showAlert('error', err.message || 'Failed to save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleOpenDoc = async (type) => {
    setLoadingDoc(type);
    try {
      await openApplicationDocument(app.id, type);
    } catch (err) {
      showAlert('error', `Failed to open ${type === 'cv' ? 'CV' : 'cover letter'}: ${err.message}`);
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete application from ${app.first_name} ${app.last_name}? This cannot be undone.`)) return;
    try {
      await onDelete(app.id);
    } catch (err) {
      showAlert('error', err.message || 'Failed to delete.');
    }
  };

  const submittedDate = new Date(app.submitted_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric'
  });

  return (
    <ApplicationCard status={app.status}>
      <CardHeader onClick={() => setExpanded(e => !e)}>
        <CardLeft>
          <ApplicantName>{app.first_name} {app.last_name}</ApplicantName>
          <ApplicantMeta>
            <span>📧 {app.email}</span>
            {app.phone && <span>📞 {app.phone}</span>}
            <span>📅 {submittedDate}</span>
            <span>🎯 {app.role_title}</span>
          </ApplicantMeta>
        </CardLeft>
        <CardRight>
          <StatusBadge status={app.status}>{app.status}</StatusBadge>
          <ExpandToggle expanded={expanded}>▼</ExpandToggle>
        </CardRight>
      </CardHeader>

      <CardBody expanded={expanded}>
        {localAlert && (
          <Alert type={localAlert.type} style={{ marginTop: '1rem' }}>
            {localAlert.message}
          </Alert>
        )}

        <DocumentRow>
          <DocButton
            onClick={() => handleOpenDoc('cv')}
            disabled={loadingDoc === 'cv'}
          >
            {loadingDoc === 'cv' ? '…' : '📄'} {app.cv_file_name || 'CV'}
          </DocButton>
          <DocButton
            onClick={() => handleOpenDoc('cover')}
            disabled={loadingDoc === 'cover'}
          >
            {loadingDoc === 'cover' ? '…' : '📝'} {app.cover_file_name || 'Cover Letter'}
          </DocButton>
          {app.portfolio && (
            <DocButton as="a" href={app.portfolio} target="_blank" rel="noopener noreferrer">
              🔗 Portfolio
            </DocButton>
          )}
        </DocumentRow>

        <BodyGrid>
          <InfoBlock>
            <InfoLabel>Contact</InfoLabel>
            <InfoValue>
              {app.email}<br />
              {app.phone || 'Phone not provided'}
            </InfoValue>
          </InfoBlock>
          <InfoBlock>
            <InfoLabel>Role</InfoLabel>
            <InfoValue>{app.role_title}</InfoValue>
          </InfoBlock>
        </BodyGrid>

        {app.cover_note && (
          <CoverNoteBlock>
            <InfoLabel style={{ marginBottom: '0.5rem' }}>Cover Note</InfoLabel>
            <CoverNoteText>{app.cover_note}</CoverNoteText>
          </CoverNoteBlock>
        )}

        <ActionsRow>
          {app.status !== 'reviewing'   && <ActionButton variant="review"    onClick={() => handleStatusChange('reviewing')}>🔍 Mark Reviewing</ActionButton>}
          {app.status !== 'shortlisted' && <ActionButton variant="shortlist" onClick={() => handleStatusChange('shortlisted')}>✅ Shortlist</ActionButton>}
          {app.status !== 'offered'     && <ActionButton variant="offer"     onClick={() => handleStatusChange('offered')}>🎉 Make Offer</ActionButton>}
          {app.status !== 'rejected'    && <ActionButton variant="reject"    onClick={() => handleStatusChange('rejected')}>❌ Reject</ActionButton>}
          {app.status !== 'pending'     && <ActionButton variant="pending"   onClick={() => handleStatusChange('pending')}>⏳ Reset to Pending</ActionButton>}
          <ActionButton variant="delete" onClick={handleDelete}>🗑 Delete</ActionButton>
        </ActionsRow>

        <NotesSection>
          <NotesLabel>Internal Notes</NotesLabel>
          <NotesInput
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add internal notes about this applicant…"
          />
          <SaveNotesButton onClick={handleSaveNotes} disabled={savingNotes}>
            {savingNotes ? 'Saving…' : 'Save Notes'}
          </SaveNotesButton>
        </NotesSection>
      </CardBody>
    </ApplicationCard>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ApplicationsModeration = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllApplications();
      setApplications(data);
    } catch (err) {
      showAlert('error', `Failed to load applications: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleStatusChange = async (id, status, notes) => {
    await updateApplicationStatus(id, status, notes);
    setApplications(prev =>
      prev.map(a => a.id === String(id) || a.id === id
        ? { ...a, status, notes }
        : a
      )
    );
    showAlert('success', 'Application updated.');
  };

  const handleDelete = async (id) => {
    await deleteApplication(id);
    setApplications(prev => prev.filter(a => a.id !== String(id) && a.id !== id));
    showAlert('success', 'Application deleted.');
  };

  const roleOptions = [...new Map(
    applications
      .filter(a => a.role_id)
      .map(a => [a.role_id, a.role_title || a.role_id])
  ).entries()].sort((a, b) => a[1].localeCompare(b[1]));

  const filtered = applications.filter(a => {
    const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
    const matchesRole = filterRole === 'all' || a.role_id === filterRole;
    const q = search.toLowerCase();
    const matchesSearch = !q || [
      a.first_name, a.last_name, a.email, a.phone, a.role_title
    ].some(v => v && v.toLowerCase().includes(q));
    return matchesStatus && matchesRole && matchesSearch;
  });

  // Stats
  const counts = STATUS_OPTIONS.reduce((acc, { value }) => {
    acc[value] = applications.filter(a => a.status === value).length;
    return acc;
  }, {});

  return (
    <Wrapper>
      {alert && <Alert type={alert.type}>{alert.message}</Alert>}

      <StatsRow>
        <StatChip>
          📋 {applications.length} total
        </StatChip>
        {STATUS_OPTIONS.map(({ value, label }) => counts[value] > 0 && (
          <StatChip key={value} status={value}>
            {label}: {counts[value]}
          </StatChip>
        ))}
      </StatsRow>

      <ToolBar>
        <SearchInput
          placeholder="Search by name, email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          {STATUS_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </FilterSelect>
        <FilterSelect value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="all">All Roles</option>
          {roleOptions.map(([id, title]) => (
            <option key={id} value={id}>{title}</option>
          ))}
        </FilterSelect>
        <RefreshButton onClick={loadApplications} disabled={loading}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </RefreshButton>
      </ToolBar>

      {loading ? (
        <EmptyState>Loading applications…</EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>
          {applications.length === 0
            ? 'No applications received yet.'
            : 'No applications match your current filter.'}
        </EmptyState>
      ) : (
        filtered.map(app => (
          <AppCard
            key={app.id}
            app={app}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        ))
      )}
    </Wrapper>
  );
};

export default ApplicationsModeration;
